import { useAppDispatch } from '@/state/hooks';
import { markAsSeen, Notification } from '@/state/reducers/notificationSlice';
import cn from 'classnames';
import React, { Fragment, useEffect, useRef } from 'react';
import Notifications from './Notifications';

interface NotificationItemProps {
    notification: Notification;
    onViewed: (id: string) => void;
    viewed: boolean;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({ notification, viewed, onViewed }) => {
    const dispatch = useAppDispatch();

    const itemRef = useRef<HTMLDivElement>(null);

    const handleClick = () => {
        if (!notification.seen) dispatch(markAsSeen(notification.id));
    };

    useEffect(() => {
        if (notification.seen || viewed) return; // Skip if already seen or viewed
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        onViewed(notification.id);
                        observer.disconnect();
                    }
                });
            },
            {
                threshold: 0.5
            }
        );
        if (itemRef.current) {
            observer.observe(itemRef.current);
        }
        return () => {
            observer.disconnect();
        };
    }, [notification.id, notification.seen, viewed, onViewed]);

    return (
        <div
            ref={itemRef}
            className={cn(
                'relative flex gap-3 hover:bg-brand-neutral-700 text-brand-text p-2 sm:px-4 border-b cursor-pointer transition-colors border-brand-neutral-700',
                {
                    'bg-brand-background-dark': !notification.seen
                }
            )}
            onClick={handleClick}>
            {!notification.seen && (
                <Fragment>
                    <span className="absolute top-2 left-7 sm:left-9 h-1.5 w-1.5 rounded-full opacity-75 animate-ping bg-yellow-400" />
                    <span className="absolute top-2 left-7 sm:left-9 h-1.5 w-1.5 rounded-full bg-yellow-500 flex-shrink-0" />
                </Fragment>
            )}
            <Notifications.Accent notification={notification} />
            <div className="flex flex-col w-full text-sm sm:text-base">
                <div className="flex w-full justify-between gap-2">
                    <div className={cn('line-clamp-2', { 'font-semibold': !notification.seen })}>
                        {notification.title}
                    </div>
                    <div className="text-sm sm:text-base text-brand-neutral-400 whitespace-nowrap flex flex-col items-end">
                        <Notifications.Stamp notification={notification} />
                    </div>
                </div>
                <div className="text-brand-neutral-400 whitespace-pre-wrap text-sm sm:text-base">
                    {notification.body}
                </div>
            </div>
        </div>
    );
};
